import {
  useMemo,
  type PropsWithChildren,
  ReactElement,
  isValidElement,
  Children,
  cloneElement,
} from "react"

import { AXIS_HEIGHT, Axis } from "./Axis"
import { BAND_TOTAL_HEIGHT, Band, BandProps } from "./Band"
import * as d3 from "d3"
import { SalaryIcon } from "./SalaryIcon"
import { Money } from "@oysterhr/schema"
import { DimensionsProvider } from "./DimensionsProvider"
import { useChartResizer } from "./useChartResizer"
import { CHART_TOP_MARGIN, findBestMultiplierForTicks } from "./utils"
import { BandChartData, BandChartSettings } from "./types"
import { NoDataBand } from "./NoDataBand"

const defaultChartSettings = {
  // When the width is 0, the container div will take the full width of the parent component
  width: 0,
  marginRight: 50,
  marginLeft: 50,
  marginTop: 10,
  marginBottom: 40,
  roundValues: false,
  minWidth: 1024,
}

export type BandChartProps = PropsWithChildren<{
  currencyCode: string
  salary?: Money | null
  data: BandChartData
  chartSettings?: BandChartSettings
}>

export const BandChart = ({
  data,
  currencyCode,
  salary,
  chartSettings,
  children: _children,
}: BandChartProps) => {
  const chartHeight = data.length * BAND_TOTAL_HEIGHT + AXIS_HEIGHT + CHART_TOP_MARGIN
  const mergedChartSettings = { ...defaultChartSettings, ...chartSettings, height: chartHeight }

  const [ref, dms] = useChartResizer(mergedChartSettings)

  const lowestAmount = Math.min(
    ...data.map(d => (d?.p10 ? d.p10 : d?.min ?? -1)).filter(d => d !== -1),
  )
  const highestAmount = Math.max(
    ...data.map(d => (d?.p90 ? d.p90 : d?.max ?? -1)).filter(d => d !== -1),
  )

  const multiplier = findBestMultiplierForTicks(data, 8)

  // Get the lowest number that is a multiple of 10000 before salaries.min
  const lowestTick = Math.floor((lowestAmount - multiplier) / multiplier) * multiplier

  const highestTick = Math.ceil((highestAmount + multiplier) / multiplier) * multiplier

  // THe unit of how many units belong to a pixel
  const scale = (highestTick - lowestTick) / dms.boundedWidth

  const xScale = useMemo(
    () => d3.scaleLinear().domain([lowestTick, highestTick]).range([0, dms.boundedWidth]),
    [lowestTick, highestTick, dms.boundedWidth],
  )

  const children = Children.map(_children, child => {
    if (isValidElement(child)) {
      return cloneElement<BandProps>(child as ReactElement, {
        roundValues: mergedChartSettings?.roundValues,
      })
    }
    return child
  })

  return (
    <div className="w-full" style={{ minWidth: mergedChartSettings?.minWidth }} ref={ref}>
      <DimensionsProvider value={{ ...dms, scale, lowestTick, highestTick }}>
        <svg
          className="rounded-lg border border-info-lighten-100 bg-info-lighten-100/10"
          width={dms.width}
          height={chartHeight}
        >
          <g transform={`translate(${dms.marginLeft}, ${dms.marginTop})`}>
            <g transform={`translate(${0}, ${dms.boundedHeight})`}>
              <Axis
                domain={xScale.domain()}
                range={xScale.range()}
                roundValues={mergedChartSettings?.roundValues}
              />
            </g>

            {/* Band needs to go after the Axis so it appears on top of the lines */}
            {children}

            {/* Only show the SalaryIcon if there's only one data point */}
            {salary && data.length === 1 && <SalaryIcon salary={salary} />}

            {/* Only show the Currency Badge if the values are not rounded */}
            {chartSettings?.roundValues && (
              <g>
                <rect
                  height={20}
                  width={36}
                  rx={10}
                  ry={10}
                  dy={20}
                  x={-40}
                  className="fill-none stroke-info-base"
                />
                <text
                  dy={20 - 6}
                  dx={18}
                  x={-40}
                  textAnchor="middle"
                  fontSize={12}
                  className="fill-info-darken-100 stroke-info-darken-100"
                >
                  {currencyCode}
                </text>
              </g>
            )}
          </g>
        </svg>
      </DimensionsProvider>
    </div>
  )
}

BandChart.Band = Band
BandChart.NoDataBand = NoDataBand
